import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from 'api/api';
import { formatServerDateTime } from 'services/dates/serverTime';
import translations from 'services/translations/translations';

interface IActivity {
  at: string;
  name: string;
}

/**
 * Small text of who looked at the offer last (someone else) and who saved it last, and
 * when. Read once when the positions are shown; the visits are kept as the log of the
 * offer for everyone who can read it, also with the right to view only.
 */
const OfferLastActivity = ({
  isAdditionalOffer,
  offerId,
}: {
  isAdditionalOffer: boolean;
  offerId: number;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.offers]);
  const [activity, setActivity] = useState<{
    lastEdited: IActivity | null;
    lastViewed: IActivity | null;
  } | null>(null);

  useEffect(() => {
    let active = true;
    api
      .get(
        `/api/v1/${
          isAdditionalOffer ? 'additionaloffers' : 'offers'
        }/${offerId}/last-activity`
      )
      .then((result: typeof activity) => {
        if (active) {
          setActivity(result);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isAdditionalOffer, offerId]);

  if (activity === null || (!activity.lastViewed && !activity.lastEdited)) {
    return null;
  }

  const line = (key: string, shown: IActivity | null) =>
    shown
      ? t(`${translations.offers}:lastActivity.${key}`, {
          at: formatServerDateTime(shown.at),
          name: shown.name,
        })
      : null;

  return (
    <span className="offer-last-activity">
      {[
        line('Viewed', activity.lastViewed),
        line('Edited', activity.lastEdited),
      ]
        .filter(Boolean)
        .join(' · ')}
    </span>
  );
};

export default OfferLastActivity;
